/**
 * Start-up decisions of the auth layer (pure, tested in Node; used by `auth.tsx`).
 *
 * - The inactivity lock is decided BEFORE the activity timestamp is refreshed: reopening the app
 *   after 10 minutes shows /lock, it does not count as a touch.
 * - A logout done without network leaves the HttpOnly cookie valid on the server: the device keeps
 *   a "logout pending" flag and, until `POST /auth/logout` succeeds (or the server answers 401),
 *   never enters with that cookie, even if `/auth/me` still answers with the user.
 */
import type { MeResponse } from 'core';
import { ApiError } from './api';
import { LOCK_AFTER_MS, isLockDue } from './auth-utils';

export type AuthStatus = 'unknown' | 'authenticated' | 'anonymous' | 'locked';

export interface LockInput {
  hasPin: boolean;
  /** `carte:locked` is '1' (locked before a reload, or by another tab). */
  lockedFlag: boolean;
  /** Last touch saved on the device (`carte:last-activity`), before refreshing it. */
  lastActivityMs: number | null;
  nowMs: number;
  lockAfterMs?: number;
}

/** True when the session must open on the lock screen. */
export function shouldStartLocked(input: LockInput): boolean {
  if (!input.hasPin) return false;
  return (
    input.lockedFlag ||
    isLockDue(input.lastActivityMs, input.nowMs, true, input.lockAfterMs ?? LOCK_AFTER_MS)
  );
}

/**
 * Outcome of `POST /auth/logout` (`err` null when it succeeded): the logout is done when the
 * server closed the session or says there is none (401); anything else (no network, 5xx) is
 * retried later.
 */
export function logoutCompleted(err: unknown): boolean {
  if (err == null) return true;
  return err instanceof ApiError && err.status === 401;
}

export interface StartupInput extends Omit<LockInput, 'hasPin'> {
  /** A logout is still to be completed on the server (`carte:logout-pending`). */
  logoutPending: boolean;
  /** Answer of `GET /auth/me`, null when it failed. */
  me: MeResponse | null;
  /** Why `/auth/me` failed: 401, or server not reachable (network, 5xx). */
  meFailure: 'unauthorized' | 'unreachable' | null;
  /** Last user kept on the device (offline PWA). */
  cachedMe: MeResponse | null;
  status: AuthStatus;
}

export type StartupDecision =
  /** Logout pending: login page with the "uscita registrata sul dispositivo" notice. */
  | { kind: 'logged-out' }
  | { kind: 'session'; me: MeResponse; offline: boolean; locked: boolean }
  /** No network and no known user at the first check: offline screen. */
  | { kind: 'boot-error' }
  /** Nothing to change (401 handled by `api:unauthorized`, or offline after a logout). */
  | { kind: 'keep' };

/** What to do with the answer of `/auth/me` (at start, when the network is back, from another tab). */
export function decideStartup(input: StartupInput): StartupDecision {
  if (input.logoutPending) return { kind: 'logged-out' };
  const lock = (me: MeResponse) =>
    shouldStartLocked({
      hasPin: me.hasPin,
      lockedFlag: input.lockedFlag,
      lastActivityMs: input.lastActivityMs,
      nowMs: input.nowMs,
      lockAfterMs: input.lockAfterMs,
    });
  if (input.me) return { kind: 'session', me: input.me, offline: false, locked: lock(input.me) };
  if (input.meFailure !== 'unreachable') return { kind: 'keep' };
  if (input.cachedMe && input.status !== 'anonymous') {
    return { kind: 'session', me: input.cachedMe, offline: true, locked: lock(input.cachedMe) };
  }
  return input.status === 'unknown' ? { kind: 'boot-error' } : { kind: 'keep' };
}
